import React, { useState } from 'react'
import {Download, Send, CreditCard, Sparkles, Image as ImageIcon, FileText} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import { useInvoices } from '@/hooks/useInvoices'

interface InvoiceItem {
  description: string
  quantity: number
  rate: number
}

const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' }
]

const InvoiceGenerator = () => {
  const { createInvoice } = useInvoices()
  const [businessName, setBusinessName] = useState('')
  const [clientName, setClientName] = useState('')
  const [invoiceNumber, setInvoiceNumber] = useState('')
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0])
  const [currency, setCurrency] = useState('USD')
  const [items, setItems] = useState<InvoiceItem[]>([
    { description: '', quantity: 1, rate: 0 }
  ])
  const [logoUrl, setLogoUrl] = useState('')
  const [logoPrompt, setLogoPrompt] = useState('')
  const [generatingLogo, setGeneratingLogo] = useState(false)
  const [taxRate, setTaxRate] = useState(0)

  const getCurrencySymbol = () => {
    return CURRENCIES.find(c => c.code === currency)?.symbol || '$'
  }

  const addItem = () => {
    setItems([...items, { description: '', quantity: 1, rate: 0 }])
  }

  const updateItem = (index: number, field: keyof InvoiceItem, value: string | number) => {
    const newItems = [...items]
    newItems[index] = { ...newItems[index], [field]: value }
    setItems(newItems)
  }

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index))
  }

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + (item.quantity * item.rate), 0)
  }

  const calculateTax = () => {
    return calculateSubtotal() * (taxRate / 100)
  }

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax()
  }

  const handleGenerateLogo = async () => {
    if (!businessName) {
      alert('Please enter your business name first')
      return
    }

    if (!logoPrompt) {
      alert('Please describe your desired logo')
      return
    }

    setGeneratingLogo(true)
    try {
      const result = await lumi.functions.invoke('generateLogo', {
        method: 'POST',
        body: {
          logoPrompt,
          businessName
        }
      })

      if (result.success && result.logoUrl) {
        setLogoUrl(result.logoUrl)
        alert('Logo generated successfully!')
      } else {
        alert('Failed to generate logo. Please try again.')
      }
    } catch (error: any) {
      const errorMessage = error?.data?.error || 'Failed to generate logo. Please try again.'
      alert(errorMessage)
    } finally {
      setGeneratingLogo(false)
    }
  }

  const handleDownloadPDF = async () => {
    if (!businessName || !clientName || !invoiceNumber) {
      alert('Please fill in all required fields (Business Name, Client Name, Invoice Number)')
      return
    }

    try {
      const result = await lumi.functions.invoke('generatePDF', {
        method: 'POST',
        body: {
          businessName,
          clientName,
          invoiceNumber,
          invoiceDate,
          items,
          totalAmount: calculateTotal(),
          currency
        }
      })

      // Save invoice to database
      const user = lumi.auth.user
      await createInvoice({
        businessName,
        clientName,
        invoiceNumber,
        invoiceDate,
        items,
        subtotalAmount: calculateSubtotal(),
        taxRate,
        taxAmount: calculateTax(),
        totalAmount: calculateTotal(),
        currency,
        status: 'draft',
        userId: user?.userId,
        pdfUrl: result.invoiceId,
        logoUrl: logoUrl || undefined
      })

      // Convert base64 to blob and download
      const pdfBlob = base64ToBlob(result.pdfBase64, 'application/pdf')
      const url = URL.createObjectURL(pdfBlob)
      const a = document.createElement('a')
      a.href = url
      a.download = `invoice-${invoiceNumber}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      alert('Invoice PDF generated, saved, and downloaded successfully!')
    } catch (error: any) {
      const errorMessage = error?.data?.error || 'Failed to generate PDF. Please try again.'
      alert(errorMessage)
    }
  }

  const base64ToBlob = (base64: string, type: string) => {
    const binary = atob(base64)
    const array = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) {
      array[i] = binary.charCodeAt(i)
    }
    return new Blob([array], { type })
  }

  const handleEmailInvoice = async () => {
    if (!businessName || !clientName || !invoiceNumber) {
      alert('Please fill in all required fields (Business Name, Client Name, Invoice Number)')
      return
    }

    const recipientEmail = prompt('Enter recipient email address:')
    if (!recipientEmail) return

    try {
      // Generate PDF first
      const pdfResult = await lumi.functions.invoke('generatePDF', {
        method: 'POST',
        body: {
          businessName,
          clientName,
          invoiceNumber,
          invoiceDate,
          items,
          totalAmount: calculateTotal(),
          currency
        }
      })

      // Save invoice to database
      const user = lumi.auth.user
      await createInvoice({
        businessName,
        clientName,
        invoiceNumber,
        invoiceDate,
        items,
        subtotalAmount: calculateSubtotal(),
        taxRate,
        taxAmount: calculateTax(),
        totalAmount: calculateTotal(),
        currency,
        status: 'sent',
        userId: user?.userId,
        pdfUrl: pdfResult.invoiceId,
        logoUrl: logoUrl || undefined
      })

      // Send email
      await lumi.functions.invoke('emailInvoice', {
        method: 'POST',
        body: {
          invoiceId: pdfResult.invoiceId,
          recipientEmail,
          pdfBase64: pdfResult.pdfBase64,
          invoiceNumber
        }
      })

      alert(`Invoice saved and email sent successfully to ${recipientEmail}!`)
    } catch (error: any) {
      const errorMessage = error?.data?.error || 'Failed to send email. Please ensure the recipient is a registered user.'
      alert(errorMessage)
    }
  }

  const handlePayNow = async () => {
    if (!businessName || !clientName || !invoiceNumber) {
      alert('Please fill in all required fields (Business Name, Client Name, Invoice Number)')
      return
    }

    try {
      const result = await lumi.functions.invoke('createCheckoutSession', {
        method: 'POST',
        body: {
          businessName,
          clientName,
          invoiceNumber,
          invoiceDate,
          items,
          totalAmount: calculateTotal(),
          currency
        }
      })

      // Save invoice to database with pending_payment status
      const user = lumi.auth.user
      await createInvoice({
        businessName,
        clientName,
        invoiceNumber,
        invoiceDate,
        items,
        subtotalAmount: calculateSubtotal(),
        taxRate,
        taxAmount: calculateTax(),
        totalAmount: calculateTotal(),
        currency,
        status: 'pending_payment',
        userId: user?.userId,
        stripeSessionId: result.sessionId,
        logoUrl: logoUrl || undefined
      })

      // Open Stripe checkout in new tab
      const win = window.open()
      if (win) {
        win.location.href = result.checkoutUrl
      } else {
        window.location.href = result.checkoutUrl
      }
    } catch (error: any) {
      const errorMessage = error?.data?.error || 'Failed to create payment session. Please try again.'
      alert(errorMessage)
    }
  }

  const handleDownloadTemplate = async (format: 'csv' | 'html' | 'txt') => {
    const filled = !!(businessName && clientName && invoiceNumber)
    
    try {
      const result = await lumi.functions.invoke('generateTemplate', {
        method: 'POST',
        body: {
          format,
          businessName: businessName || '[Your Business Name]',
          clientName: clientName || '[Client Name]',
          invoiceNumber: invoiceNumber || '[INV-001]',
          invoiceDate: invoiceDate,
          items: items.length > 0 && items[0].description ? items : [{ description: '[Service/Product]', quantity: 1, rate: 0 }],
          totalAmount: calculateTotal(),
          currency,
          filled
        }
      })

      if (result.success) {
        // Create blob and download
        const blob = new Blob([result.content], { type: result.contentType })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = result.filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)

        alert(`Template downloaded successfully as ${result.filename}!`)
      }
    } catch (error: any) {
      const errorMessage = error?.data?.error || 'Failed to generate template. Please try again.'
      alert(errorMessage)
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-xl border-2 border-[#0B5394] p-6 md:p-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
        Invoice Generator Widget
      </h2>

      {/* AI Logo Generator Section */}
      <div className="mb-6 p-6 bg-gradient-to-br from-blue-50 to-green-50 rounded-lg border-2 border-dashed border-[#10B981]">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-[#10B981]" />
          <h3 className="text-lg font-bold text-gray-900">AI Logo Generator (Optional)</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Describe Your Logo
            </label>
            <input
              type="text"
              value={logoPrompt}
              onChange={(e) => setLogoPrompt(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#10B981] focus:border-transparent"
              placeholder="e.g., Modern tech company, blue and green colors, minimalist cloud icon"
              disabled={generatingLogo}
            />
          </div>
          <button
            onClick={handleGenerateLogo}
            disabled={generatingLogo || !businessName || !logoPrompt}
            className="bg-gradient-to-r from-[#10B981] to-[#047857] hover:from-[#059669] hover:to-[#065f46] text-white px-6 py-2 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generatingLogo ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Generating...
              </>
            ) : (
              <>
                <ImageIcon className="w-5 h-5" />
                Generate Logo
              </>
            )}
          </button>
        </div>

        {logoUrl && (
          <div className="mt-4 flex items-center gap-4">
            <div className="w-24 h-24 rounded-lg border-2 border-gray-300 overflow-hidden bg-white flex items-center justify-center">
              <img src={logoUrl} alt="Generated logo" className="w-full h-full object-contain" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-green-700 mb-1">✓ Logo generated successfully!</p>
              <p className="text-xs text-gray-600">This logo will be included in your invoice PDF</p>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Business Info */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Your Business Name *
          </label>
          <input
            type="text"
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B5394] focus:border-transparent"
            placeholder="Your Company LLC"
          />
        </div>

        {/* Client Info */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Client Name *
          </label>
          <input
            type="text"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B5394] focus:border-transparent"
            placeholder="Client Company"
          />
        </div>

        {/* Invoice Number */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Invoice Number *
          </label>
          <input
            type="text"
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B5394] focus:border-transparent"
            placeholder="INV-001"
          />
        </div>

        {/* Invoice Date */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Invoice Date *
          </label>
          <input
            type="date"
            value={invoiceDate}
            onChange={(e) => setInvoiceDate(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B5394] focus:border-transparent"
          />
        </div>

        {/* Currency Selector */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Currency *
          </label>
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B5394] focus:border-transparent"
          >
            {CURRENCIES.map((curr) => (
              <option key={curr.code} value={curr.code}>
                {curr.symbol} {curr.code} - {curr.name}
              </option>
            ))}
          </select>
        </div>

        {/* Tax Rate */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Tax Rate (%)
          </label>
          <input
            type="number"
            value={taxRate}
            onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B5394] focus:border-transparent"
            placeholder="0"
            min="0"
            max="100"
            step="0.01"
          />
        </div>
      </div>

      {/* Line Items */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Invoice Items</h3>
        
        {items.map((item, index) => (
          <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4 items-end">
            <div className="md:col-span-5">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <input
                type="text"
                value={item.description}
                onChange={(e) => updateItem(index, 'description', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B5394] focus:border-transparent"
                placeholder="Service or product"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quantity
              </label>
              <input
                type="number"
                value={item.quantity}
                onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B5394] focus:border-transparent"
                min="0"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rate ({getCurrencySymbol()})
              </label>
              <input
                type="number"
                value={item.rate}
                onChange={(e) => updateItem(index, 'rate', parseFloat(e.target.value) || 0)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B5394] focus:border-transparent"
                min="0"
                step="0.01"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Total
              </label>
              <div className="px-4 py-2 bg-gray-100 rounded-lg font-semibold text-gray-900">
                {getCurrencySymbol()}{(item.quantity * item.rate).toFixed(2)}
              </div>
            </div>
            <div className="md:col-span-1">
              {items.length > 1 && (
                <button
                  onClick={() => removeItem(index)}
                  className="w-full px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        ))}

        <button
          onClick={addItem}
          className="text-[#0B5394] hover:text-[#047857] font-semibold text-sm"
        >
          + Add Another Item
        </button>
      </div>

      {/* Total */}
      <div className="flex justify-end mb-6">
        <div className="w-full md:w-1/2 space-y-2">
          <div className="flex justify-between items-center text-gray-700">
            <span className="font-medium">Subtotal:</span>
            <span className="font-semibold">{getCurrencySymbol()}{calculateSubtotal().toFixed(2)}</span>
          </div>
          {taxRate > 0 && (
            <div className="flex justify-between items-center text-gray-700">
              <span className="font-medium">Tax ({taxRate}%):</span>
              <span className="font-semibold">{getCurrencySymbol()}{calculateTax().toFixed(2)}</span>
            </div>
          )}
          <div className="bg-[#047857] text-white rounded-lg p-4">
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold">Total Amount:</span>
              <span className="text-2xl font-bold">{getCurrencySymbol()}{calculateTotal().toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={handleDownloadPDF}
            className="flex-1 bg-[#0B5394] hover:bg-[#084273] text-white px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
          >
            <Download className="w-5 h-5" />
            Download PDF
          </button>
          <button
            onClick={handleEmailInvoice}
            className="flex-1 bg-[#047857] hover:bg-[#059669] text-white px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
          >
            <Send className="w-5 h-5" />
            Email Invoice
          </button>
          <button
            onClick={handlePayNow}
            className="flex-1 bg-gradient-to-r from-[#10B981] to-[#047857] hover:from-[#059669] hover:to-[#065f46] text-white px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all shadow-lg hover:shadow-xl"
          >
            <CreditCard className="w-5 h-5" />
            Pay Now
          </button>
        </div>

        {/* Template Download Options */}
        <div className="border-t-2 border-gray-200 pt-4">
          <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Or Download as Editable Template:
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => handleDownloadTemplate('html')}
              className="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-700 px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors border-2 border-blue-200"
            >
              📄 Word Format (HTML)
            </button>
            <button
              onClick={() => handleDownloadTemplate('csv')}
              className="flex-1 bg-green-50 hover:bg-green-100 text-green-700 px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors border-2 border-green-200"
            >
              📊 Excel Format (CSV)
            </button>
            <button
              onClick={() => handleDownloadTemplate('txt')}
              className="flex-1 bg-gray-50 hover:bg-gray-100 text-gray-700 px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors border-2 border-gray-300"
            >
              📝 Text Format (TXT)
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2 text-center">
            Templates will be {businessName && clientName ? 'pre-filled with your data' : 'blank for manual editing'}
          </p>
        </div>
      </div>

      <p className="text-center text-sm text-gray-600 mt-4">
        ✓ No sign-up required • ✓ 100% free • ✓ Instant professional invoices • ✓ Secure payment
      </p>
    </div>
  )
}

export default InvoiceGenerator